import { useState, useEffect } from 'react';
import { testSgpReadOnlyFlow, testRadius, listSgpOperations } from '../api/sgp';
import { getCompanies } from '../api/companies';
import { getDiagnostics } from '../api/integrations';
import { Topbar } from '../layout/Topbar';
import type { DiagnosticsResult, Company } from '../types';

export function SgpDashboardPage() {
  const [tab, setTab] = useState<'diagnostics' | 'radar' | 'operations'>('diagnostics');

  // ── Diagnostics ──
  const [diagnostics, setDiagnostics] = useState<DiagnosticsResult[]>([]);
  const [diagLoading, setDiagLoading] = useState(false);

  // ── Read-only test ──
  const [testDoc, setTestDoc] = useState('');
  const [testResult, setTestResult] = useState<Record<string, unknown> | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  // ── RADIUS test ──
  const [radUser, setRadUser] = useState('');
  const [radDoc, setRadDoc] = useState('');
  const [radCompanyId, setRadCompanyId] = useState('');
  const [radResult, setRadResult] = useState<Record<string, unknown> | null>(null);
  const [radLoading, setRadLoading] = useState(false);

  // ── Companies ──
  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    getCompanies().then((list) => {
      setCompanies(list);
      if (list.length > 0) setRadCompanyId(list[0].id);
    }).catch(() => {});
  }, []);

  // ── Operations ──
  const [operations, setOperations] = useState<Record<string, unknown>[]>([]);
  const [opsLoading, setOpsLoading] = useState(false);

  const handleDiagnostics = async () => {
    setDiagLoading(true);
    try {
      const data = await getDiagnostics();
      setDiagnostics(data);
    } catch (err) { console.error('Falha ao carregar diagnóstico:', err); } finally {
      setDiagLoading(false);
    }
  };

  const handleTestReadOnly = async () => {
    if (!testDoc.trim()) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const data = await testSgpReadOnlyFlow(undefined, testDoc);
      setTestResult(data);
    } catch (err: any) {
      setTestResult({ ok: false, error: err.message || 'Falha no teste' });
    } finally {
      setTestLoading(false);
    }
  };

  const handleTestRadius = async () => {
    if (!radUser.trim()) return;
    setRadLoading(true);
    setRadResult(null);
    try {
      const data = await testRadius(radUser, radCompanyId || undefined, radDoc || undefined);
      setRadResult(data);
    } catch (err: any) {
      setRadResult({ ok: false, error: err.message || 'Falha no teste RADIUS' });
    } finally {
      setRadLoading(false);
    }
  };

  const handleListOperations = async () => {
    setOpsLoading(true);
    try {
      const data = await listSgpOperations();
      setOperations(data as Record<string, unknown>[]);
    } catch (err) { console.error('Falha ao listar operações:', err); } finally {
      setOpsLoading(false);
    }
  };

  return (
    <div className="view-container">
      <Topbar title="SGP Operations" subtitle="Diagnóstico, testes e catálogo de operações SGP." />

      <div className="flex gap-sm" style={{ marginBottom: 16 }}>
        <button className={tab === 'diagnostics' ? '' : 'secondary'} onClick={() => setTab('diagnostics')} type="button">🔍 Diagnóstico</button>
        <button className={tab === 'radar' ? '' : 'secondary'} onClick={() => setTab('radar')} type="button">📡 RADIUS Test</button>
        <button className={tab === 'operations' ? '' : 'secondary'} onClick={() => setTab('operations')} type="button">📋 Catálogo</button>
      </div>

      {/* ═══════ DIAGNOSTICS TAB ═══════ */}
      {tab === 'diagnostics' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="flex gap-sm">
            <button onClick={handleDiagnostics} disabled={diagLoading}>
              {diagLoading ? '🔄' : '🔄'} Atualizar diagnóstico
            </button>
            <button className="secondary" onClick={handleTestReadOnly} disabled={testLoading || !testDoc.trim()}>
              Testar fluxo read-only
            </button>
          </div>

          {/* Read-only test input */}
          <div className="panel">
            <div className="panel-body">
              <div className="flex gap-sm" style={{ alignItems: 'flex-end' }}>
                <label style={{ flex: 1 }}>
                  CPF/CNPJ para teste
                  <input value={testDoc} onChange={(e) => setTestDoc(e.target.value)} placeholder="Apenas números" />
                </label>
              </div>
            </div>
          </div>

          {/* Read-only test result */}
          {testResult && (
            <div className="panel">
              <div className="panel-header">
                <h2>Resultado do diagnóstico read-only</h2>
                <span className={`pill ${(testResult as any).ok ? 'success' : 'danger'}`}>
                  {(testResult as any).ok ? 'OK' : 'Falha'}
                </span>
              </div>
              <div className="panel-body">
                <pre className="payload-preview">{JSON.stringify(testResult, null, 2)}</pre>
              </div>
            </div>
          )}

          {/* Company diagnostics */}
          {diagnostics.map((diag) => (
            <div key={diag.id} className="panel">
              <div className="panel-header">
                <h2>{diag.name}</h2>
                <span className={`pill ${diag.readyForTelegramSgp ? 'success' : 'warning'}`}>
                  {diag.readyForTelegramSgp ? '✅ Pronto' : '⚠️ Incompleto'}
                </span>
              </div>
              <div className="panel-body">
                <div className="detail-section">
                  <span className="detail-section-title">🤖 Bots</span>
                  {diag.bots.length === 0 ? (
                    <p className="text-sm text-secondary">Nenhum bot ativo</p>
                  ) : (
                    diag.bots.map((bot) => (
                      <div key={bot.id} className="detail-row" style={{ padding: '4px 0' }}>
                        <span className="label">@{bot.username}</span>
                        <span className={`pill ${bot.isActive ? 'success' : 'neutral'}`}>{bot.provider}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className="detail-section">
                  <span className="detail-section-title">🔌 SGP</span>
                  {diag.sgp.length === 0 ? (
                    <p className="text-sm text-secondary">Nenhuma credencial SGP</p>
                  ) : (
                    diag.sgp.map((sgp) => (
                      <div key={sgp.id} className="detail-row" style={{ padding: '4px 0' }}>
                        <span className="label">{sgp.baseUrl || 'URL não configurada'}</span>
                        <span className={`pill ${sgp.ready ? 'success' : 'danger'}`}>
                          {sgp.ready ? 'OK' : `Falta: ${!sgp.hasToken ? 'Token ' : ''}${!sgp.hasApp ? 'App ' : ''}`}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ))}

          {diagnostics.length === 0 && !diagLoading && (
            <div className="panel">
              <div className="empty-state">
                <p>Clique em "Atualizar diagnóstico" para verificar o status das empresas.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════ RADIUS TAB ═══════ */}
      {tab === 'radar' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="panel" style={{ maxWidth: 500 }}>
            <div className="panel-body">
              <h3 style={{ marginBottom: 8 }}>📡 Teste de sessão RADIUS</h3>
              <div className="form-stack">
                {companies.length > 1 && (
                  <label>
                    Empresa (credencial SGP)
                    <select value={radCompanyId} onChange={(e) => setRadCompanyId(e.target.value)}>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </label>
                )}
                <label>
                  Usuário PPPoE
                  <input value={radUser} onChange={(e) => setRadUser(e.target.value)} placeholder="login do cliente" />
                </label>
                <label>
                  Documento (opcional)
                  <input value={radDoc} onChange={(e) => setRadDoc(e.target.value)} placeholder="CPF/CNPJ do cliente" />
                </label>
                <button onClick={handleTestRadius} disabled={radLoading || !radUser.trim() || !radCompanyId}>
                  {radLoading ? 'Consultando...' : 'Consultar sessão RADIUS'}
                </button>
              </div>
            </div>
          </div>

          {radResult && (
            <div className="panel">
              <div className="panel-header">
                <h2>Resultado RADIUS</h2>
                <span className={`pill ${(radResult as any).ok ? 'success' : (radResult as any).error ? 'danger' : 'warning'}`}>
                  {(radResult as any).ok ? 'Sessão encontrada' : (radResult as any).error ? 'Erro' : 'Sem sessão'}
                </span>
              </div>
              <div className="panel-body">
                {(radResult as any).mapped ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    {(radResult as any).rawSessionSample && (
                      <div className="sgp-card" style={{ borderLeft: '3px solid var(--color-primary, #0d6efd)' }}>
                        <div className="sgp-card-title">👤 Dados do Cliente</div>
                        <div className="sgp-card-row"><span>Nome</span><strong>{(radResult as any).rawSessionSample.nome || '—'}</strong></div>
                        <div className="sgp-card-row"><span>Login PPPoE</span><span className="mono">{(radResult as any).rawSessionSample.pppoe_login || '—'}</span></div>
                        <div className="sgp-card-row"><span>Plano</span><span>{(radResult as any).rawSessionSample.plano || '—'}</span></div>
                        <div className="sgp-card-row"><span>Endereço</span><span>{(radResult as any).rawSessionSample.endereco || '—'}</span></div>
                        <div className="sgp-card-row"><span>Cidade/UF</span><span>{(radResult as any).rawSessionSample.endereco_cidade ? `${(radResult as any).rawSessionSample.endereco_cidade}/${(radResult as any).rawSessionSample.endereco_uf || ''}` : '—'}</span></div>
                        <div className="sgp-card-row"><span>Serviço ID</span><span className="mono">{(radResult as any).rawSessionSample.servico_id || '—'}</span></div>
                      </div>
                    )}
                    <div className="sgp-card" style={{ borderLeft: `3px solid ${(radResult as any).mapped.online ? 'var(--color-success, #198754)' : 'var(--color-neutral, #6c757d)'}` }}>
                      <div className="sgp-card-title">📡 Sessão RADIUS</div>
                      <div className="sgp-card-row">
                        <span>Status</span>
                        <span className={`pill ${(radResult as any).mapped.online ? 'success' : 'neutral'}`}>
                          {(radResult as any).mapped.online ? '🟢 Online' : '⚪ Offline'}
                        </span>
                      </div>
                      <div className="sgp-card-row"><span>IP atribuído</span><span className="mono">{(radResult as any).mapped.framedIpAddress || '—'}</span></div>
                      <div className="sgp-card-row"><span>MAC</span><span className="mono">{(radResult as any).mapped.callingStationId || '—'}</span></div>
                      <div className="sgp-card-row"><span>NAS</span><span>{(radResult as any).mapped.nas || '—'}</span></div>
                      <div className="sgp-card-row"><span>Porta</span><span>{(radResult as any).mapped.nasPortId || '—'}</span></div>
                      <div className="sgp-card-row"><span>Tempo online</span><strong>{(radResult as any).mapped.sessionTime || '—'}</strong></div>
                      <div className="sgp-card-row"><span>Início da sessão</span><span>{(radResult as any).mapped.startTime ? new Date((radResult as any).mapped.startTime).toLocaleString('pt-BR') : '—'}</span></div>
                      <div className="sgp-card-row"><span>Download</span><span>{(radResult as any).mapped.inputOctets != null ? `${((radResult as any).mapped.inputOctets / 1024 / 1024 / 1024).toFixed(2)} GB` : '—'}</span></div>
                      <div className="sgp-card-row"><span>Upload</span><span>{(radResult as any).mapped.outputOctets != null ? `${((radResult as any).mapped.outputOctets / 1024 / 1024 / 1024).toFixed(2)} GB` : '—'}</span></div>
                    </div>
                  </div>
                ) : (
                  <div className="sgp-card">
                    <div className="sgp-card-row"><span>Status</span><span className={`pill ${(radResult as any).ok ? 'success' : 'danger'}`}>{(radResult as any).ok ? 'OK' : 'Falha'}</span></div>
                    <div className="sgp-card-row"><span>Mensagem</span><span>{(radResult as any).message || (radResult as any).error || '—'}</span></div>
                  </div>
                )}
                <details style={{ marginTop: 16 }}>
                  <summary style={{ cursor: 'pointer', fontSize: '0.786rem', color: 'var(--text-secondary)' }}>Ver JSON completo</summary>
                  <pre className="payload-preview" style={{ marginTop: 8, fontSize: '0.786rem', maxHeight: 300, overflow: 'auto' }}>{JSON.stringify(radResult, null, 2)}</pre>
                </details>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════ OPERATIONS TAB ═══════ */}
      {tab === 'operations' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="flex gap-sm">
            <button onClick={handleListOperations} disabled={opsLoading}>
              {opsLoading ? '🔄' : '📋'} Listar operações
            </button>
          </div>

          {operations.length > 0 ? (
            <div className="panel">
              <div className="panel-header">
                <h2>Catálogo de Operações SGP</h2>
                <span className="text-sm text-secondary">{operations.length} operações</span>
              </div>
              <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nome</th>
                      <th>Método</th>
                      <th>Path</th>
                      <th>Risco</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operations.map((op: any) => (
                      <tr key={op.id}>
                        <td><code style={{ fontSize: 11 }}>{op.id}</code></td>
                        <td>{op.name}</td>
                        <td><span className="pill neutral">{op.method}</span></td>
                        <td style={{ fontSize: 11, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{op.path}</td>
                        <td>
                          <span className={`pill ${op.risk === 'CRITICAL' ? 'danger' : op.risk === 'SENSITIVE_WRITE' ? 'warning' : op.risk === 'SAFE_WRITE' ? 'primary' : 'neutral'}`}>
                            {op.risk}
                          </span>
                        </td>
                        <td>
                          <span className={`pill ${op.status === 'IMPLEMENTED' ? 'success' : 'neutral'}`}>
                            {op.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            !opsLoading && (
              <div className="panel">
                <div className="empty-state">
                  <p>Clique em "Listar operações" para ver o catálogo.</p>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
